import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Upload, Building2, Globe, Share2, DollarSign, Phone, Clock } from "lucide-react";
import { getLocalTimezone } from "../lib/timezone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TIMEZONES = Intl.supportedValuesOf('timeZone');

// Full currency list with symbols
const CURRENCIES = [
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "LKR", symbol: "Rs", name: "Sri Lankan Rupee" },
  { code: "NPR", symbol: "Rs", name: "Nepalese Rupee" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "PKR", symbol: "Rs", name: "Pakistani Rupee" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
];

export default function Settings() {
  const { organization, setOrganization } = useAuth();
  const [form, setForm] = useState({
    name: organization?.name || "",
    email: organization?.email || "",
    phone: organization?.phone || "",
    website: organization?.website || "",
    logo: organization?.logo || "",
    description: organization?.description || "",
    location: organization?.location || "",
    whatsapp: organization?.whatsapp || "",
    facebook: organization?.facebook || "",
    instagram: organization?.instagram || "",
    twitter: organization?.twitter || "",
    currency: organization?.currency || "INR",
    timezone: organization?.timezone || getLocalTimezone(),
  });
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [timezoneOpen, setTimezoneOpen] = useState(false);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("logo", res.data.url);
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await api.put("/settings/organization", form);
      setOrganization(res.data.organization);
      toast.success("Settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const selectedCurrency = CURRENCIES.find((c) => c.code === form.currency) || CURRENCIES[0];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight">Settings</h1>

      {/* ── Gym Details ── */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />Gym Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-2">
            <div className="w-24 h-24 rounded-sm bg-secondary flex items-center justify-center overflow-hidden border border-border">
              {logoPreview || form.logo
                ? <img src={logoPreview || `${BACKEND_URL}${form.logo}`} alt="Logo" className="w-full h-full object-cover" />
                : <Upload className="h-8 w-8 text-muted-foreground" />}
            </div>
            <div>
              <Label htmlFor="logo" className="cursor-pointer text-primary text-sm hover:underline">Upload Logo</Label>
              <input id="logo" type="file" accept="image/*" className="hidden" onChange={handleLogo} data-testid="logo-upload" />
              <p className="text-xs text-muted-foreground mt-1">Recommended: 200×200px</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Gym Name *</Label><Input value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-sm mt-1" data-testid="gym-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm mt-1" data-testid="gym-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="rounded-sm mt-1" /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={(e) => update("website", e.target.value)} className="rounded-sm mt-1" placeholder="https://yourgym.com" /></div>
          </div>

          <div>
            <Label>Location / Address</Label>
            <Textarea
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              className="rounded-sm mt-1"
              rows={3}
              placeholder="123, Main Street, City, State - Pincode"
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="rounded-sm mt-1" rows={3} placeholder="Tell members about your gym..." />
          </div>
        </CardContent>
      </Card>

      {/* ── Currency & Timezone ── */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />Regional Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                <SelectTrigger className="rounded-sm mt-1">
                  <SelectValue>
                    <span>{selectedCurrency.symbol} — {selectedCurrency.code} ({selectedCurrency.name})</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="font-mono mr-2 text-primary">{c.symbol}</span>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Currently using: <span className="font-semibold text-primary">{selectedCurrency.symbol}</span> ({selectedCurrency.code})
              </p>
            </div>

            <div>
              <Label>Timezone</Label>
              <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={timezoneOpen}
                    className="w-full justify-between rounded-sm mt-1 font-normal font-sans border-input bg-background"
                  >
                    {form.timezone ? form.timezone.replace(/_/g, ' ') : "Select timezone..."}
                    <Clock className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput className="outline-none focus:border-0 focus:ring-0 rounded-none" placeholder="Search timezone..." />
                    <CommandList>
                      <CommandEmpty>No timezone found.</CommandEmpty>
                      <CommandGroup className="max-h-60 overflow-y-auto">
                        {TIMEZONES.map((tz) => (
                          <CommandItem
                            key={tz}
                            value={tz}
                            onSelect={() => {
                              update("timezone", tz);
                              setTimezoneOpen(false);
                            }}
                          >
                            {tz.replace(/_/g, ' ')}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground mt-1">
                Used for display times across the platform
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── WhatsApp ── */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />WhatsApp
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>WhatsApp Number</Label>
              <Input
                value={form.whatsapp}
                onChange={(e) => update("whatsapp", e.target.value)}
                className="rounded-sm mt-1"
                placeholder="+91 98765 43210"
              />
              <p className="text-xs text-muted-foreground mt-1">Used for member communication and quick links</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Social Media ── */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />Social Media
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Facebook</Label>
              <Input
                value={form.facebook}
                onChange={(e) => update("facebook", e.target.value)}
                className="rounded-sm mt-1"
                placeholder="https://facebook.com/yourgym"
              />
            </div>
            <div>
              <Label>Instagram</Label>
              <Input
                value={form.instagram}
                onChange={(e) => update("instagram", e.target.value)}
                className="rounded-sm mt-1"
                placeholder="https://instagram.com/yourgym"
              />
            </div>
            <div>
              <Label>X (Twitter)</Label>
              <Input
                value={form.twitter}
                onChange={(e) => update("twitter", e.target.value)}
                className="rounded-sm mt-1"
                placeholder="https://x.com/yourgym"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={loading} className="rounded-sm w-full md:w-auto" data-testid="save-settings-btn">
        {loading ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}
